import {
  deleteDataByUrl,
  getDataByUrl,
  ID,
  postDataByUrl,
  updateDataByUrl
} from 'requests';

export const getCandidateOverview = async (candidate_id: ID): Promise<any> =>
  getDataByUrl(`/api/candidate/overview/${candidate_id}/`);

export const getCandidateRemind = async (candidate_id: ID): Promise<any> =>
  getDataByUrl(`/api/candidate/${candidate_id}/remind/`);

export const getCandidateMindgageRemind = async (candidate_id: ID): Promise<any> =>
  getDataByUrl(`/api/candidate/${candidate_id}/remind/mindgage/`);

export const getCandidateData = async (
  candidate_id: ID,
  route?: string
): Promise<any> => {
  let url = `/api/candidate/${candidate_id}/`;
  if (route) url = `${url}${route}/`;
  return getDataByUrl(url);
};

export const getCandidateAssessment = async (id: ID): Promise<any> =>
  getDataByUrl(`/api/assessment/assessments/result/?user_id=${id}`);

export const postCandidateData = async (
  candidate_id: ID,
  data: any,
  route: string
): Promise<any> =>
  postDataByUrl(`/api/candidate/${candidate_id}/${route}/`, data);

export const postCustomerData = async (
  customer_id: ID,
  data: any,
  route: string
): Promise<any> =>
  postDataByUrl(`/api/customers/${customer_id}/${route}/`, data);

export const updateCandidateData = async (
  candidate_id: ID,
  id: ID,
  data: any,
  route: string
): Promise<any> =>
  updateDataByUrl(`/api/candidate/${candidate_id}/${route}/${id}/`, data);

export const submitCandidateData = async (
  candidate_id: ID,
  formData: FormData,
  route: string,
  id?: ID
): Promise<any> =>
  (id ? updateDataByUrl : postDataByUrl)(
    `/api/candidate/${candidate_id}/${route}${id ? `/${id}` : ''}/`,
    formData
  );

export const updateCustomerNotes = async (
  customer_id: ID,
  id: ID,
  data: any,
  route: string
): Promise<any> =>
  updateDataByUrl(`/api/customers/${customer_id}/${route}/${id}/`, data);

export const updateCandidateMainInfo = async (
  candidate_id: ID,
  formData: FormData,
  onProgress?: (streamingData: any) => void
): Promise<any> => {
  if (!onProgress) {
    // Fallback to regular request if no progress callback
    return updateDataByUrl(`/api/candidate/${candidate_id}/`, formData);
  }

  try {
    const response = await fetch(`/api/candidate/${candidate_id}/`, {
      method: 'PATCH',
      body: formData,
      headers: {
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const processStream = async (): Promise<void> => {
      const readChunk = async (): Promise<void> => {
        const { done: readerDone, value } = await reader.read();

        if (readerDone) return;

        if (value) {
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.trim() === '') {
              // eslint-disable-next-line no-continue
              continue;
            }

            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                onProgress(data);
              } catch (e) {
                console.warn('Failed to parse streaming data:', line);
              }
            }
          }
        }

        // Continue reading
        await readChunk();
      };

      await readChunk();
    };

    await processStream();

    // Return final response (this might need to be handled differently based on your API)
    return { success: true };
  } catch (error) {
    console.error('Streaming request failed:', error);
    // Fallback to regular request
    return updateDataByUrl(`/api/candidate/${candidate_id}/`, formData);
  }
};

export const deleteCandidateData = async (
  candidate_id: string | number,
  id: string | number,
  route: string
): Promise<any> =>
  deleteDataByUrl(`/api/candidate/${candidate_id}/${route}/${id}/`);

export const deleteCustomerData = async (
  customer_id: string | number,
  id: string | number,
  route: string
): Promise<any> =>
  deleteDataByUrl(`/api/customers/${customer_id}/${route}/${id}/`);

export const updateApplicationData = async (id: ID, data: any): Promise<any> =>
  updateDataByUrl(`/api/vacancy/applications/${id}/manage/`, data);

export const cancelCandidateApplication = async (id: ID): Promise<any> =>
  updateDataByUrl(`/api/vacancy/applications/${id}/cancel/`, {});

export const getAssessmentTestLink = async (user: ID): Promise<any> =>
  postDataByUrl(`/api/assessment/assessments/`, { user });

export const getAllCandidate = async (
  user: ID,
  role: string,
  q: string,
  currentPage: number,
  limit: number
): Promise<any> =>
  getDataByUrl(
    `/api/account/?edit=true&limit=${limit}&offset=${
      limit * currentPage - limit
    }&role=${role}&q=${q}`,
    { user }
  );

export const createCandidate = async (data: FormData): Promise<any> =>
  postDataByUrl('/api/candidate/recruiter/create/', data);

export const sendInvitation = async (candidate_id: ID): Promise<any> =>
  postDataByUrl(`/api/account/${candidate_id}/invite/`);

export const editCandidate = async (
  data: FormData,
  candidate_id: ID
): Promise<any> =>
  updateDataByUrl(`/api/candidate/recruiter/${candidate_id}/`, data);

export const deleteCandidate = async (candidate_id: ID): Promise<any> =>
  deleteDataByUrl(`/api/candidate/recruiter/${candidate_id}/`);
